/// FOURJS_START_COPYRIGHT(D,2015)
/// Property of Four Js*
/// (c) Copyright Four Js 2015, 2026. All Rights Reserved.
/// * Trademark of Four Js Development Tools Europe Ltd
///   in the United States and elsewhere
///
/// This file can be modified by licensees according to the
/// product manual.
/// FOURJS_END_COPYRIGHT

"use strict";

modulum("StartMenuCommandWidget", ["TextWidgetBase", "WidgetFactory"], function (context, cls) {
  /**
   * StartMenuCommand widget.
   * @class StartMenuCommandWidget
   * @memberOf classes
   * @extends classes.TextWidgetBase
   */
  cls.StartMenuCommandWidget = context.oo.Class(cls.TextWidgetBase, function ($super) {
    return /** @lends classes.StartMenuCommandWidget.prototype */ {
      __name: "StartMenuCommandWidget",

      /**
       * Image of the startMenu command
       * @protected
       * @type {classes.ImageWidget}
       */
      _image: null,

      /**
       * Fallback icon text element shown when no image is set
       * @protected
       * @type {?HTMLElement}
       */
      _iconText: null,

      /**
       * Startmenu command is trigger with double-click
       * @type {boolean}
       */
      _triggerByDoubleClick: false,

      /**
       * treeitem <button> (cached at init)
       * @type {HTMLElement}
       */
      _buttonElement: null,

      /**
       * @inheritDoc
       */
      _initElement: function () {
        $super._initElement.call(this);
        this._buttonElement = this._element.getElementsByTagName("button")[0];
        this.setAcceptEventWhenWindowInactive(true);
        // If theme defines double-click trigger, command is trigger only this way
        this._triggerByDoubleClick = context.ThemeService.getValue("gbc-StartMenuWidget-doubleclick-trigger");
      },

      /**
       * The role-bearing element is the inner treeitem <button>, not the
       * <li role="none"> root.
       * @inheritDoc
       */
      getAriaElement: function () {
        return this._buttonElement;
      },

      /** @inheritDoc */
      _ariaDisabledExposed: true,

      /**
       * @inheritDoc
       */
      getDOMTabIndexElements: function () {
        return this._buttonElement ? [this._buttonElement] : [];
      },

      /**
       * @inheritDoc
       */
      manageMouseClick: function (domEvent) {
        if (!this._triggerByDoubleClick) {
          this.emit(context.constants.widgetEvents.click);
        }
        return false;
      },

      /**
       * @inheritDoc
       */
      manageMouseDblClick: function (domEvent) {
        if (this._triggerByDoubleClick) {
          this.emit(context.constants.widgetEvents.click);
        }
        return false;
      },

      /**
       * Keyboard activation of the focused command (accessibility mode routes
       * keys to the DOM-focused widget before dialog accelerators).
       * A single Enter/Space always fires, even in the double-click-trigger
       * theme where the mouse path needs a double-click: there is no keyboard
       * "double-Enter", so single activation is the accessible mapping.
       * @inheritDoc
       */
      managePriorityKeyDown: function (keyString, domKeyEvent, repeat) {
        if (this.isEnabled() && this._isActivationKey(keyString, repeat)) {
          this.emit(context.constants.widgetEvents.click);
          return true;
        }
        return $super.managePriorityKeyDown.call(this, keyString, domKeyEvent, repeat);
      },

      /**
       * @inheritDoc
       */
      destroy: function () {
        this._buttonElement = null;
        if (this._image) {
          this._image.destroy();
          this._image = null;
        }
        this.removeIconText();
        $super.destroy.call(this);
      },

      /**
       * Set the text of the command
       * @param {string} text
       */
      setText: function (text) {
        this._setTextContent(
          text,
          function () {
            return this._element.getElementsByClassName("gbc_StartMenuCommandWidget__text")[0];
          }.bind(this)
        );
        if (!this._iconText && !this._image) {
          this.setIconText(text);
        }
      },

      /**
       * Get the text of the command
       * @return {string}
       */
      getText: function () {
        return this._element.getElementsByClassName("gbc_StartMenuCommandWidget__text")[0].textContent;
      },

      /**
       * Set the title to appear as tooltip
       * @param {string} title
       */
      setTitle: function (title) {
        this._element.setAttribute("title", title);
      },

      /**
       * Get the title to appear as tooltip
       * @return {string}
       */
      getTitle: function () {
        return this._element.getAttribute("title");
      },

      /**
       * Define the command image
       * @param {string} image
       */
      setImage: function (image) {
        if (image.length !== 0) {
          if (!this._image) {
            this._image = cls.WidgetFactory.createWidget("Image", this.getBuildParameters());
            cls.DOMElementHelper.prependChild(this._element, this._image.getElement());
          }
          this._image.setSrc(image);
          this.removeIconText();
        }
      },

      /**
       * Get image of the command
       * @return {?string} - source of the image, null if not set
       */
      getImage: function () {
        if (this._image) {
          return this._image.getSrc();
        }
        return null;
      },

      /**
       * Create and display the icon text element based on the command name
       * @param {string} text
       */
      setIconText: function (text) {
        const testContainer = document.createElement("div");
        testContainer.classList.add(this.getBEMPartName("iconText"));
        testContainer.textContent = this._generateIconText(text);
        this._iconText = testContainer;
        cls.DOMElementHelper.prependChild(this._element, testContainer);
      },

      /**
       * Remove the icon text element from DOM if present
       */
      removeIconText: function () {
        if (this._iconText) {
          this._iconText.remove();
          delete this._iconText;
        }
      },

      /**
       * Generate icon text from startMenuCommand text
       * @param {string} text
       * @returns {string}
       */
      _generateIconText: function (text) {
        if (!text) return "";

        // Split the text into words by treating any non-letter and non-number character as a separator.
        const words = text.split(/[^\p{L}\p{N}]+/u).filter(Boolean);

        if (words.length >= 3) {
          // Take first letter of first 3 words
          return words
            .slice(0, 3)
            .map((w) => w[0])
            .join("")
            .toUpperCase();
        }

        // If less than 3 words, take first 3 letters from cleaned full string
        const flattened = words.join("");
        return flattened.slice(0, 3).toUpperCase();
      }
    };
  });
  cls.WidgetFactory.registerBuilder("StartMenuCommand", cls.StartMenuCommandWidget);
});
